import { defineStore } from 'pinia';

// M29.2 Voice Experience — state for the Nexceo::Voice Foundation-backed
// call UI. Deliberately separate from stores/calls.js (the legacy,
// Twilio-only store keyed by callSid/isActive) — this store is keyed by the
// backend `Call` id and speaks the new, standardized voice.* event
// vocabulary (Nexceo::Voice::WebsocketEvents), not the legacy `voice_call.*`
// events.
//
// No CRM data lives here — no lead_id, no kanban stage, no site visit. Just
// enough to drive the call UI: id, provider, state, capabilities, the
// conversation/agent it's attached to, and a transient notification queue.

export const TERMINAL_CALL_STATES = ['ended', 'failed', 'missed', 'rejected'];
export const RINGING_STATES = ['started', 'ringing'];
export const CONNECTED_STATES = ['accepted', 'connected'];

export const useNexceoVoiceStore = defineStore('nexceoVoice', {
  state: () => ({
    // callId -> call record
    calls: {},
    // 'connected' | 'reconnecting' | 'disconnected' — driven by ActionCable
    // connection lifecycle, surfaced by the Connection Status indicator.
    connectionStatus: 'connected',
    // Transient list for the Call Notification UI — missed calls, failures.
    notifications: [],
  }),

  getters: {
    allCalls: state => Object.values(state.calls),

    activeCall: state =>
      Object.values(state.calls).find(call =>
        CONNECTED_STATES.includes(call.state)
      ) || null,

    ringingCalls: state =>
      Object.values(state.calls).filter(call =>
        RINGING_STATES.includes(call.state)
      ),

    incomingRingingCalls: state =>
      Object.values(state.calls).filter(
        call =>
          RINGING_STATES.includes(call.state) && call.direction === 'incoming'
      ),

    missedCalls: state =>
      Object.values(state.calls).filter(call => call.state === 'missed'),

    hasActiveCall: state =>
      Object.values(state.calls).some(
        call =>
          RINGING_STATES.includes(call.state) ||
          CONNECTED_STATES.includes(call.state)
      ),

    callForConversation: state => conversationId =>
      Object.values(state.calls).find(
        call =>
          call.conversationId === conversationId &&
          !TERMINAL_CALL_STATES.includes(call.state)
      ) || null,
  },

  actions: {
    // Normalizes either a REST response (snake_case) or an ActionCable
    // broadcast payload (also snake_case, see Nexceo::Voice::Broadcaster)
    // into the store's camelCase shape, merging onto any existing record so
    // a later, partial update (e.g. a broadcast with no `capabilities`)
    // never clobbers fields set by an earlier REST response.
    upsertCall(payload) {
      const callId = payload?.callId ?? payload?.call_id;
      if (!callId) return;

      const existing = this.calls[callId] || {};
      const nextCall = {
        callId,
        provider: payload.provider ?? existing.provider,
        state: payload.state ?? existing.state,
        previousState:
          payload.previous_state ??
          payload.previousState ??
          existing.previousState,
        reason: payload.reason ?? existing.reason ?? null,
        conversationId:
          payload.conversation_id ??
          payload.conversationId ??
          existing.conversationId,
        agentId: payload.agent_id ?? payload.agentId ?? existing.agentId,
        capabilities: payload.capabilities ?? existing.capabilities ?? null,
        direction: payload.direction ?? existing.direction ?? null,
        status: payload.status ?? existing.status,
      };

      this.calls = { ...this.calls, [callId]: nextCall };

      // M29.2.1 — only notify on an actual state *change* into a terminal
      // state, not every time upsertCall happens to be called with an
      // already-terminal state (a duplicate/redelivered ActionCable
      // broadcast, a rehydration response for a call the store already
      // knew was terminal, etc.) — otherwise a single missed call could
      // surface as several identical notifications.
      const stateChanged = existing.state !== nextCall.state;
      if (stateChanged && TERMINAL_CALL_STATES.includes(nextCall.state)) {
        this.pushNotification(nextCall);
      }
    },

    // Handler for the seven standardized ActionCable events
    // (Nexceo::Voice::WebsocketEvents). `eventName` is the full
    // 'voice.xxx' string; the state it represents is everything after the dot.
    handleWebsocketEvent(eventName, data) {
      const state = eventName.replace('voice.', '');
      this.upsertCall({ ...data, state });
    },

    // M29.2.1 Voice Reliability — reconciles local state against the
    // authoritative backend list of currently-active calls (from
    // NexceoVoiceAPI#listActiveSessions). Two things happen:
    //  1. every session in the fresh list is upserted (picks up anything
    //     that happened while this tab was gone/disconnected — a missed
    //     ActionCable event, a call another tab/agent already handled).
    //  2. any call this tab still has as non-terminal, but that the fresh
    //     list no longer includes, is removed — the backend is
    //     authoritative on what's still active; its absence means the call
    //     ended one way or another while we weren't watching, so there is
    //     nothing to keep showing as ringing/connected. No notification is
    //     pushed for these (removed, not upserted) — the exact terminal
    //     reason isn't known from an index response, and never labeling
    //     wrong beats guessing.
    rehydrate(sessions) {
      const freshIds = new Set(sessions.map(session => session.call_id));

      Object.values(this.calls).forEach(call => {
        if (
          !freshIds.has(call.callId) &&
          !TERMINAL_CALL_STATES.includes(call.state)
        ) {
          this.removeCall(call.callId);
        }
      });

      sessions.forEach(session => this.upsertCall(session));
    },

    removeCall(callId) {
      const { [callId]: _removed, ...rest } = this.calls;
      this.calls = rest;
    },

    setConnectionStatus(status) {
      this.connectionStatus = status;
    },

    pushNotification(call) {
      this.notifications = [
        ...this.notifications,
        { id: `${call.callId}-${call.state}-${Date.now()}`, call: { ...call } },
      ];
    },

    dismissNotification(id) {
      this.notifications = this.notifications.filter(
        notification => notification.id !== id
      );
    },

    reset() {
      this.calls = {};
      this.notifications = [];
    },
  },
});
